import { FC, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Input, Modal, TableColumnsType, Flex } from "antd";
import { AnyObject } from "antd/lib/_util/type";
import { ColumnsType } from "antd/lib/table";

import { locationList } from "@/apis/inventoryv2";
import useDataList from "@/hooks/useDataList";

import { CommonListParams } from "@/types/common";
import { LocationLockEnum, WLocation } from "@/types/inventoryV2";

import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import SText from "@/styles/SText";
import { getLocationCode } from "@/pages/Warehouse/utils";
import { LockOutlined, UnlockOutlined } from "@ant-design/icons";
import { getLocationIdx } from "@/utils/wLocation";
import useLabelColor from "@/pages/Warehouse/pages/InventoryManage/hooks/useLabelColor";
import { DictContext } from "@/layout/context/DictContext";
import SelectLocationType from "./SelectLocationType";

interface LocationSelectProps {
  value?: string;
  onChange?: (location: WLocation) => void;
  open: boolean;
  onCancel: () => void;
}

const LocationSelect: FC<LocationSelectProps> = ({ value, onChange, open, onCancel }) => {
  const { t } = useTranslation();
  const { getLabelColor } = useLabelColor();
  const { locationTypes } = useContext(DictContext);

  const tableRef = useRef<TableLayoutRef | null>(null);
  const [list, total, listLoading, { fetchList }] = useDataList<WLocation, [CommonListParams, number | undefined]>({
    fetchApi: locationList
  });
  const [selectedLocation, setSelectedLocation] = useState<WLocation | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | undefined>(value);
  const [keyword, setKeyword] = useState<string>("");
  const [locationType, setLocationType] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (open) {
      fetchLocations(0, "");
      setSelectedKey(value);
    }
  }, [open, value]);

  const fetchLocations = (offset: number, keyword: string, type?: number) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
    if (offset === 0) tableRef.current?.resetTablePage();
    fetchList({
      body: keyword,
      pagination: { offset, take },
    }, type);
  };

  const columns: TableColumnsType<WLocation> = [
    {
      title: t("warehouse.locationIndex"),
      dataIndex: "l1",
      key: "l1",
      width: 120,
      render: (text, record) => <SText text={getLocationIdx(record)} />
    },
    {
      title: <SText text={t("warehouse.locationCode")} />,
      key: "locationCode",
      width: 240,
      fixed: "left",
      render: (_, record) => (
        <SText text={getLocationCode(record)} />
      ),
    },
    {
      title: t("warehouse.labels"),
      key: "labels",
      width: 300,
      render: (_, record) => (
        <Flex gap={4} wrap="wrap">
          {record.label1 && <SText tagStyle={{ flex: 1 }} textColor="#FFF" tagMode color={getLabelColor("label1")} text={record.label1} />}
          {record.label2 && <SText tagStyle={{ flex: 1 }} textColor="#FFF" tagMode color={getLabelColor("label2")} text={record.label2} />}
          {record.label3 && <SText tagStyle={{ flex: 1 }} textColor="#FFF" tagMode color={getLabelColor("label3")} text={record.label3} />}
          {record.label4 && <SText tagStyle={{ flex: 1 }} textColor="#FFF" tagMode color={getLabelColor("label4")} text={record.label4} />}
          {record.label5 && <SText tagStyle={{ flex: 1 }} textColor="#FFF" tagMode color={getLabelColor("label5")} text={record.label5} />}
        </Flex>
      )
    },
    {
      title: t('counting.lockState'),
      dataIndex: 'lockState',
      key: 'lockState',
      width: 120,
      render: (lockState: LocationLockEnum) => {
        const isLocked = lockState === LocationLockEnum.LOCKED;
        return (
          <SText tagMode
            tagIcon={isLocked ? <LockOutlined /> : <UnlockOutlined />}
            color={isLocked ? 'red' : 'green'}
            text={isLocked ? t('counting.counting') : t('counting.notCounting')}
          />
        );
      },
    },
    {
      title: t("inventory.locationType"),
      key: "type",
      width: 140,
      render: (_, record) => {
        let info = locationTypes.find(type => type.key === record.type);
        return (
          info && <SText text={info.desc} />
        )
      }
    },
    {
      title: t("common.desc"),
      dataIndex: "desc",
      key: "desc",
      width: 200,
      render: (text) => <SText text={text || ''} />
    },
  ];

  const handleOk = () => {
    if (selectedLocation && onChange) {
      onChange(selectedLocation);
    }
    onCancel();
  };

  const handleCancel = () => {
    setSelectedLocation(null);
    setSelectedKey(undefined);
    setKeyword("");
    setLocationType(undefined);
    onCancel();
  };

  return (
    <Modal
      centered
      open={open}
      width={1000}
      onCancel={handleCancel}
      title={t("warehouse.selectLocation")}
      onOk={handleOk}
      okButtonProps={{ disabled: !selectedLocation }}
    >
      <div style={{ height: "600px" }}>
        <STableLayout
          loading={listLoading}
          dataSource={list}
          rowKey="id"
          rowSelection={{
            type: "radio",
            selectedRowKeys: selectedKey ? [selectedKey] : [],
            getCheckboxProps: (record) => ({
              disabled: record.lockState === LocationLockEnum.LOCKED,
            }),
            onChange: (_, selectedRows) => {
              const location = selectedRows[0] as WLocation;
              setSelectedKey(location?.id);
              setSelectedLocation(location);
            },
          }}
          columns={columns as ColumnsType<AnyObject>}
          scrollX={700}
          scroll={{ y: 480 }}
          headerChildren={
            <Flex gap={8} style={{ width: "100%" }}>
              <SelectLocationType
                allowClear
                value={locationType}
                style={{ width: 200 }}
                onChange={(type) => {
                  setLocationType(type);
                }}
              />
              <Input.Search
                enterButton
                placeholder={t("warehouse.searchLocation")}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onSearch={(searchValue) => {
                  fetchLocations(0, searchValue, locationType);
                  tableRef?.current?.resetTablePage();
                }}
              />
            </Flex>
          }
          tableLayoutRef={tableRef}
          total={total}
          handleChange={(offset) => {
            fetchLocations(offset, keyword, locationType);
          }}
        />
      </div>
    </Modal>
  );
};

export default LocationSelect;
